import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CurrencyEur, WarningOctagon, ArrowsClockwise } from "@phosphor-icons/react";
import { InfoHint } from "./InfoHint";
import { monthsBetween, monthlyFxPairs } from "./calculate/fxNeeds";

/**
 * v0.5.9m — Per-month national-return FX rates (BMF/ECB convention).
 *
 * National VAT returns file in the country's own currency (EU-27 → EUR).
 * Rows ingested in another currency (e.g. GBP) must be converted at the
 * TRANSACTION MONTH's rate — Germany §16(6) UStG uses the BMF monthly
 * average. This block collects one rate per covered month × foreign
 * currency, plus a free-text source (frozen into the calc as evidence).
 *
 * Rate direction is deliberately the BMF/ECB published convention:
 * FOREIGN currency per 1 EUR (a GBP rate reads ~0.8438). The live
 * preview (£100 → €X) makes an inverted entry instantly obvious.
 *
 * Shape (controlled): { CCY: { "YYYY-MM": { rate, source } } }
 */

const CCY_SYMBOL = {
  GBP: "£", EUR: "€", USD: "$",
  DKK: "kr", SEK: "kr", CZK: "Kč", PLN: "zł", HUF: "Ft", RON: "lei", BGN: "лв",
};
const symbolFor = (ccy) => CCY_SYMBOL[ccy] || ccy || "";
// The rate publisher is fixed by the FILING (target) currency, and each
// publisher prints its rate in its own currency-per-unit convention:
//   EUR filing (DE/FR/IT/…) → BMF/ECB, "[ccy] per €1"
//   GBP filing (GB)         → HMRC,    "[ccy] per £1"
//   local filing (DKK/SEK/CZK/PLN/HUF/RON/BGN) → national central bank.
// The label must mirror what the publisher prints or an operator will
// enter the reciprocal and the divide-by-rate conversion will invert.
const _NATIONAL_BANK = {
  DKK: "Danmarks Nationalbank", SEK: "Riksbank", CZK: "ČNB",
  PLN: "NBP", HUF: "MNB", RON: "BNR", BGN: "BNB",
};
const publisherFor = (target) =>
  target === "GBP" ? "HMRC" : target === "EUR" ? "BMF/ECB"
    : (_NATIONAL_BANK[target] || "national central bank");
const sourcePlaceholderFor = (target) =>
  `Source — e.g. ${publisherFor(target)} monthly rate 12/2025`;


export const MonthlyFxRates = ({
  selectedRuns, registrations, extraPairs, periodStart, periodEnd, value, setValue,
}) => {
  const { t } = useTranslation();
  const months = useMemo(
    () => monthsBetween(periodStart, periodEnd), [periodStart, periodEnd],
  );
  // Every (source → target) pair that actually needs a monthly rate:
  // each row currency against each filing currency of the client's LOCAL
  // registrations, unioned with the pairs the review queue already found
  // (an OSS destination is by definition not a local registration).
  // A2-467 — the rule lives in ./calculate/fxNeeds so the page's
  // "nothing to convert" message cannot disagree with this table.
  const pairs = useMemo(
    () => monthlyFxPairs(selectedRuns, registrations, extraPairs),
    [selectedRuns, registrations, extraPairs],
  );

  if (pairs.length === 0 || months.length === 0) return null;

  const setCell = (src, tgt, month, field, v) => {
    const next = { ...(value || {}) };
    next[src] = { ...(next[src] || {}) };
    next[src][tgt] = { ...(next[src][tgt] || {}) };
    next[src][tgt][month] = { ...(next[src][tgt][month] || {}), [field]: v };
    setValue(next);
  };

  const applyToAll = (src, tgt) => {
    const first = months.map((m) => value?.[src]?.[tgt]?.[m]).find(
      (e) => e && String(e.rate || "").trim(),
    );
    if (!first) return;
    // v0.6.10 — this used to silently overwrite EVERY month's rate
    // with the first one found, including months that already held a
    // real, DIFFERENT rate the operator deliberately typed — monthly
    // rates legitimately vary month to month, that's the entire
    // premise of this grid. Confirm before clobbering anything already
    // entered that this action would actually change.
    const wouldOverwrite = months.some((m) => {
      const existing = String(value?.[src]?.[tgt]?.[m]?.rate || "").trim();
      return existing && existing !== String(first.rate);
    });
    if (wouldOverwrite && !window.confirm(
      `This replaces every month's rate for ${src} → ${tgt} with ${first.rate} — including months that already have a different rate entered. Continue?`,
    )) {
      return;
    }
    const next = { ...(value || {}) };
    next[src] = { ...(next[src] || {}) };
    next[src][tgt] = { ...(next[src][tgt] || {}) };
    for (const m of months) {
      next[src][tgt][m] = { rate: first.rate, source: first.source || "" };
    }
    setValue(next);
  };

  const preview = (rate) => {
    const n = Number(rate);
    if (!Number.isFinite(n) || n <= 0) return null;
    return (100 / n).toFixed(2);
  };
  // Inverted-entry heuristic for the GBP<->EUR pair (either direction):
  // a sane "row per 1 filing" rate sits ~0.84 (GBP/EUR) or ~1.18 (EUR/GBP).
  // Outside [0.4, 2.5] almost certainly means the rate was entered upside
  // down.
  const isGbpEurPair = (ccy, target) => {
    const pair = new Set([ccy, target]);
    return pair.has("GBP") && pair.has("EUR");
  };
  // v0.6.10 — the band above only ever worked for GBP/EUR, since it's
  // an absolute range that assumes the rate clusters near 1.0 — true
  // for GBP/EUR historically, wildly wrong for e.g. HUF (~390) or CZK
  // (~25). Every OTHER currency pair got no sanity check at all, even
  // though "typed the reciprocal" is just as easy a mistake there.
  // Hardcoding a "correct" real-world level per currency isn't safe
  // (rates move; a stale guess is worse than no check), so instead
  // compare against the operator's OWN other entries for this exact
  // pair: a value wildly different in magnitude from what they already
  // typed elsewhere is a strong, data-driven signal of a typo or
  // inverted entry, with no external reference data needed. Only
  // applies once at least one other month has a value to compare
  // against — nothing to judge plausibility against otherwise.
  const implausible = (ccy, target, rate, otherRates) => {
    const n = Number(rate);
    if (!Number.isFinite(n) || n <= 0) return false;
    if (isGbpEurPair(ccy, target)) return n < 0.4 || n > 2.5;
    const others = (otherRates || [])
      .map(Number)
      .filter((x) => Number.isFinite(x) && x > 0);
    if (others.length === 0) return false;
    const sorted = [...others].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    return n > median * 3 || n < median / 3;
  };

  return (
    <div
      data-testid="monthly-fx-rates"
      className="border-t border-slate-200 dark:border-slate-700 px-4 py-3 bg-white dark:bg-slate-900"
    >
      <div className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400 flex items-center gap-1 mb-1">
        <CurrencyEur size={11} weight="bold" />
        {t("ui.calculate.monthly_fx.title", { defaultValue: "Per-country national returns" })}
        <InfoHint
          testid="monthly-fx-info"
          label={t("ui.calculate.monthly_fx.info", { defaultValue: "Per-country national (local) VAT returns file in the country's own currency (EU-27 → EUR, UK → GBP). Germany's rates (the BMF monthly table) and the ECB rate of the invoice day for France, the Netherlands, Portugal and Sweden are filled in automatically. Enter each MONTH's rate for the other countries — one per month, per foreign currency, in the 'foreign per 1 unit of filing currency' convention shown on each row; a rate you enter for a month is used instead of the published one, for every country, including Germany, France and the Netherlands. A missing month's rate blocks that country's return. This is legally distinct from the OSS end-of-period ECB rate above — do not copy one into the other." })}
        />
      </div>
      <p className="mb-2 text-[10px] text-slate-400 dark:text-slate-500 leading-snug">
        {t("ui.calculate.monthly_fx.separate", { defaultValue: "Separate from the OSS/UK rate above, even for the same currency pair — each country's own monthly rate." })}
      </p>
      <div className="overflow-x-auto -mx-1 px-1">
        <table
          data-testid="monthly-fx-table"
          className="border-separate border-spacing-0 text-left"
        >
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-white dark:bg-slate-900 text-[9px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 px-2 py-1 border-b border-slate-200 dark:border-slate-700 text-left">
                {t("ui.calculate.monthly_fx.col_pair", { defaultValue: "Currency pair" })}
              </th>
              {months.map((m) => (
                <th
                  key={m}
                  className="text-[9px] font-bold text-slate-500 dark:text-slate-400 tabular-nums px-1.5 py-1 border-b border-slate-200 dark:border-slate-700 min-w-[124px] text-left"
                >
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pairs.map(([ccy, target]) => (
              <tr key={`${ccy}-${target}`} data-testid={`monthly-fx-pair-${ccy}-${target}`}>
                <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 align-top px-2 py-1.5 border-b border-slate-100 dark:border-slate-800">
                  <div className="text-[11px] font-bold text-slate-700 dark:text-slate-200 tabular-nums whitespace-nowrap">
                    {ccy} → {target}
                  </div>
                  <div className="text-[9px] text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    {ccy} {t("ui.calculate.monthly_fx.per", { defaultValue: "per" })} {symbolFor(target)}1 · {publisherFor(target)}
                  </div>
                  {months.length > 1 && (
                    <button
                      type="button"
                      data-testid={`monthly-fx-apply-all-${ccy}-${target}`}
                      onClick={() => applyToAll(ccy, target)}
                      title={t("ui.calculate.monthly_fx.fill_all_title", { n: months.length, defaultValue: `Copy the first entered month's rate to all ${months.length} months` })}
                      className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold text-teal-700 dark:text-teal-300 bg-teal-600/10 hover:bg-teal-600/20 border border-teal-400/50 px-1.5 py-0.5 rounded"
                    >
                      <ArrowsClockwise size={10} weight="bold" />
                      {t("ui.calculate.monthly_fx.fill_all", { defaultValue: "Fill all" })}
                    </button>
                  )}
                </td>
                {months.map((m) => {
                  const cell = value?.[ccy]?.[target]?.[m] || {};
                  const pv = preview(cell.rate);
                  const otherRates = months
                    .filter((mm) => mm !== m)
                    .map((mm) => value?.[ccy]?.[target]?.[mm]?.rate);
                  const warn = implausible(ccy, target, cell.rate, otherRates);
                  // v0.6.10 — was `!String(cell.rate||"").trim()`, so a
                  // typed "0" (or "-1") rendered as a valid-looking
                  // teal box while the caption below simultaneously
                  // said "enter rate" — a contradictory signal. Blank
                  // is still fine (the post-calc review flow handles
                  // that); only a filled-but-invalid value is "missing".
                  const rawRate = String(cell.rate || "").trim();
                  const missing = !rawRate || !(Number.isFinite(Number(rawRate)) && Number(rawRate) > 0);
                  const rateTitle = warn
                    ? (isGbpEurPair(ccy, target)
                      ? `Rate looks inverted — ${ccy} per ${target} is usually ~0.4–2.5`
                      : `Rate looks very different from other months you entered for ${ccy} → ${target} — check for a typo or inverted entry`)
                    : undefined;
                  return (
                    <td
                      key={m}
                      className="align-top px-1.5 py-1.5 border-b border-slate-100 dark:border-slate-800"
                    >
                      <input
                        data-testid={`monthly-fx-rate-${ccy}-${target}-${m}`}
                        value={cell.rate || ""}
                        inputMode="decimal"
                        onChange={(e) => setCell(ccy, target, m, "rate", e.target.value)}
                        placeholder="0.8438"
                        title={rateTitle}
                        className={`w-full min-w-[112px] bg-white dark:bg-slate-800 border px-1.5 py-1 tabular-nums text-xs rounded ${
                          missing
                            ? "border-amber-500 text-amber-700 dark:text-amber-300"
                            : warn
                              ? "border-red-500 text-red-600 dark:text-red-400"
                              : "border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300"
                        }`}
                      />
                      <input
                        data-testid={`monthly-fx-source-${ccy}-${target}-${m}`}
                        value={cell.source || ""}
                        onChange={(e) => setCell(ccy, target, m, "source", e.target.value)}
                        placeholder={publisherFor(target)}
                        title={sourcePlaceholderFor(target)}
                        className="mt-1 w-full min-w-[112px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 text-[9px] rounded text-slate-600 dark:text-slate-300"
                      />
                      <div className="mt-0.5 text-[9px] leading-tight">
                        {warn ? (
                          <span className="text-red-600 dark:text-red-400 flex items-center gap-0.5">
                            <WarningOctagon size={9} weight="bold" /> {t("ui.calculate.monthly_fx.inverted", { defaultValue: "inverted?" })}
                          </span>
                        ) : pv ? (
                          <span className="text-slate-500 dark:text-slate-400">
                            {symbolFor(ccy)}100→{symbolFor(target)}{pv}
                          </span>
                        ) : (
                          <span className="text-amber-700 dark:text-amber-300">{t("ui.calculate.fx_enter_rate", { defaultValue: "enter rate" })}</span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
